import React, { useState } from 'react';
import { Paper, Typography, Box, Accordion, AccordionSummary, AccordionDetails, Chip, IconButton, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import { IChunk } from '../../../Types/Docs';
import { toast } from '../../../Utils/toast';

interface ChunkViewerProps {
  chunks: IChunk[];
  'data-testid'?: string;
}

const ChunkViewer: React.FC<ChunkViewerProps> = ({ chunks, 'data-testid': testId }) => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleDownload = async (documentId: string, documentName: string) => {
    setDownloadingId(documentId);
    try {
      const { downloadDocument } = await import('../../../Utils/docsApi');
      const blob = await downloadDocument(documentId);
      
      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('Received empty file');
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = documentName || 'document';
      window.document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
      }, 100);
      
      toast.success('Download started');
    } catch (error: any) {
      console.error('Download error:', error);
      const errorMessage = error?.message || error?.response?.data?.message || 'Failed to download document';
      toast.error(errorMessage);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Paper sx={{ p: 3 }} data-testid={testId}>
      <Typography variant="h3" sx={{ mb: 2 }}>
        Retrieved Chunks ({chunks.length})
      </Typography>
      <Box>
        {chunks.map((chunk, index) => (
          <Accordion
            key={index}
            expanded={expanded === `panel-${index}`}
            onChange={handleChange(`panel-${index}`)}
            sx={{ mb: 1 }}
            data-testid={`${testId}-chunk-${index}`}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={2}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {chunk.documentName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {chunk.text.substring(0, 100)}...
                  </Typography>
                </Box>
                <Chip
                  label={`${Math.round(chunk.score * 100)}%`}
                  size="small"
                  color="primary"
                  sx={{ ml: 2 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box flex={1}>
                  <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                    {chunk.text}
                  </Typography>
                  {chunk.categories && Array.isArray(chunk.categories) && chunk.categories.length > 0 && (
                    <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
                      {chunk.categories.map((category: string, idx: number) => (
                        <Chip
                          key={idx}
                          label={category}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(chunk.documentId, chunk.documentName);
                  }}
                  disabled={downloadingId === chunk.documentId}
                  aria-label={`Download ${chunk.documentName}`}
                  data-testid={`${testId}-download-${chunk.documentId}`}
                  sx={{ ml: 2 }}
                >
                  {downloadingId === chunk.documentId ? (
                    <CircularProgress size={20} />
                  ) : (
                    <DownloadIcon />
                  )}
                </IconButton>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Paper>
  );
};

export default ChunkViewer;
